import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { AlertHeader, Button, ModalContent, ModalFooter, ModalHeader, ModalLayout, ModalText, ModalTitle } from 'components';
import { theme, toRem } from 'design';
import { routeConstants } from 'routes';
import { toTitleCase } from 'utils';
import { IPayrollDataError, IPayrollIntegrationExportError, PayrollExportValidationType } from 'utils/csvformatter';

interface IPayrollTipsExportProps {
  error?: IPayrollIntegrationExportError;
  isProcessing?: boolean;
  onClose: () => void;
  onContinue?: () => void;
}

const DetailsContainer = styled.ul`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  background-color: ${theme.color.neutral[50]};
  border-radius: ${toRem(8)};
  list-style: none;
  margin-top: ${toRem(16)};
  padding: ${toRem(16)};
  width: 100%;
  text-align: center;

  &:not(:last-child) {
    margin-bottom: ${toRem(16)};
  }
`;

const ListHeader = styled.p`
  font-size: ${toRem(14)};
  font-weight: ${theme.font.weight.medium};
  padding-bottom: ${toRem(8)};
  word-break: break-all;
`;

const ErrorItem = styled.li`
  color: ${theme.color.neutral[500]};
  font-size: ${toRem(12)};
  font-weight: ${theme.font.weight.medium};

  &.bold {
    color: ${theme.color.neutral[900]};
    font-size: ${toRem(14)};
    font-weight: ${theme.font.weight.medium};
  }

  &:not(:last-child) {
    padding-bottom: ${toRem(8)};
  }
`;

const PayrollTipsExport = ({ error, isProcessing = false, onClose, onContinue }: IPayrollTipsExportProps) => {
  const [errorMessage, setErrorMessage] = useState<{ title: string; message: string }>(undefined);

  useEffect(() => {
    switch (error.type) {
      case PayrollExportValidationType.MISSING_EMPLOYEE_ID:
        setErrorMessage({
          title: 'Missing Employee IDs',
          message: "The following employees do not have an employee ID configured. The export file will contain these employees' names instead of their employee IDs.",
        });
        break;
      case PayrollExportValidationType.DUPLICATE_EMPLOYEE_ID:
        setErrorMessage({
          title: 'Duplicated Employee ID',
          message: 'You have multiple employees with the same Employee ID. These must be unique. Please update the IDs to resolve the issue.',
        });
        break;
      case PayrollExportValidationType.NO_EARNINGS_CODE:
        setErrorMessage({
          title: 'Missing Earnings Code',
          message: 'The following cannot be processed because no earnings code has been configured for these items:',
        });
        break;
      default:
        break;
    }
  }, [error]);

  const renderDuplicateGroups = () =>
    (error.data as IPayrollDataError[]).map((item) => (
      <React.Fragment key={item.employeeId}>
        <ListHeader data-cy="modal-list-header">ID: {item.employeeId}</ListHeader>
        {(item.employeeName as string[]).map((name, idx) => (
          <ErrorItem key={`${item.employeeId}-${idx}`}>{name}</ErrorItem>
        ))}
      </React.Fragment>
    ));

  const renderMissingEarningsFields = () =>
    (error.data as string[]).map((key, idx) => (
      <ErrorItem key={`${key}-${idx}`} className="bold">
        {toTitleCase(key)}
      </ErrorItem>
    ));

  return (
    <ModalLayout dataCy="payroll-export-error-modal" maxWidth={420} isActive onClose={onClose}>
      <ModalHeader>
        <ModalTitle data-cy="modal-title">Export Payroll Data</ModalTitle>
      </ModalHeader>
      <ModalContent>
        {errorMessage && (
          <>
            <AlertHeader title={errorMessage.title} />
            <ModalText data-cy="modal-message">{errorMessage.message}</ModalText>
          </>
        )}
        <DetailsContainer>
          {error?.type === PayrollExportValidationType.MISSING_EMPLOYEE_ID && (error.data as IPayrollDataError[]).map((item, idx) => <ErrorItem key={`error-item-${idx}`}>{item.employeeName}</ErrorItem>)}
          {error?.type === PayrollExportValidationType.DUPLICATE_EMPLOYEE_ID && renderDuplicateGroups()}
          {error?.type === PayrollExportValidationType.NO_EARNINGS_CODE && renderMissingEarningsFields()}
        </DetailsContainer>
        {error?.type === PayrollExportValidationType.NO_EARNINGS_CODE && <ModalText data-cy="contact-support-message">Contact Kickfin Support to have earnings codes added for these items</ModalText>}
      </ModalContent>
      {error?.type === PayrollExportValidationType.MISSING_EMPLOYEE_ID ? (
        <ModalFooter data-cy="modal-footer" columns={2}>
          <Button appearance="white" label="Cancel" onClick={onClose} />
          {onContinue && <Button label="Continue" onClick={onContinue} isProcessing={isProcessing} />}
        </ModalFooter>
      ) : (
        <ModalFooter data-cy="modal-footer">
          {error?.type === PayrollExportValidationType.NO_EARNINGS_CODE ? <Button label="Cancel" onClick={onClose} /> : <Button label="Manage Users" to={`${routeConstants.private.employees}`} />}
        </ModalFooter>
      )}
    </ModalLayout>
  );
};

export default PayrollTipsExport;
